import { matchRouteParams } from './match-route';
import type { KnownRoute } from './routes';

export type LastWorkspace = 'last' | (string & {});
export type WorkspaceIdRouteParamInput = LastWorkspace | number | null | undefined;
export type WorkspaceIdRouteParams = { workspaceId: WorkspaceIdRouteParamInput };

// All workspace routes share this prefix. `satisfies KnownRoute` ties it to the
// canonical route list in routes.ts, so removing/renaming that route fails
// typecheck here rather than silently returning the wrong workspace id.
const WORKSPACE_ROUTE_PREFIX = '/workspaces/:workspaceId' satisfies KnownRoute;
export function normalizeWorkspaceIdRouteParam(value: WorkspaceIdRouteParamInput): string {
  if (value == null || value === '' || value === 0) {
    return 'last';
  }

  return String(value);
}

export const workspaceRouteParams = {
  // Keep URL parsing permissive; stringify handles the app-side number/null
  // inputs used when building locations.
  parse: ({ workspaceId }: { workspaceId: string }): WorkspaceIdRouteParams => ({ workspaceId }),
  stringify: ({ workspaceId }: { workspaceId: WorkspaceIdRouteParamInput }) => ({
    workspaceId: normalizeWorkspaceIdRouteParam(workspaceId),
  }),
};

/**
 * Extracts the raw `workspaceId` route segment from a pathname using the
 * canonical route pattern — i.e. what `useParams().workspaceId` used to return,
 * including the `last` sentinel and any id format the route accepts. Reads from
 * the pathname rather than route matches/params because the embedded-app router
 * bridge syncs the location without a TanStack Router load on same-bundle
 * navigations, leaving matches/params stale. (CON-7180)
 */
export function getWorkspaceIdParam(pathname: string): string | null {
  return matchRouteParams(pathname, WORKSPACE_ROUTE_PREFIX, { end: false })?.workspaceId ?? null;
}
